/**
 * Methodology schema: single source of truth for report calculation methodology.
 * Used by the methodology page and can feed PDF generation to avoid drift.
 * Logic and formulas are derived from backend: app/services/reports, app/services/query/registry.
 */

export type Threshold = { label: string; range: string; tone: 'good' | 'warn' | 'bad' };

export type Metric = {
  id: string;
  title: string;
  intent?: string;
  inputs?: { name: string; source?: string; note?: string }[];
  filters?: string[];
  formula?: string; // plain text; render with whitespace-pre-wrap
  thresholds?: Threshold[];
  caveats?: string[];
};

export type MethodologySection = {
  id: string;
  label: string;
  description?: string;
  metrics?: Metric[];
  blocks?: { title: string; bullets: string[] }[]; // for non-metric sections like Data Sources / Notes
};

export const METHODOLOGY: MethodologySection[] = [
  // ─── KPI Dashboard ─────────────────────────────────────────────────────
  {
    id: 'kpi',
    label: 'KPI Dashboard',
    description: 'How the headline health score and dimension scores are derived (project.kpis).',
    metrics: [
      {
        id: 'overall-health',
        title: 'Overall Project Health Score',
        intent: 'Weighted composite of six dimensions for dashboard headline.',
        formula:
          'Overall Health = (Schedule × 0.25) + (Work Performance × 0.20) + (Resources × 0.15) + (Quality × 0.15) + (Scope × 0.15) + (Risk × 0.10)',
        thresholds: [
          { label: 'CRITICAL', range: '< 50', tone: 'bad' },
          { label: 'AT RISK', range: '50–74', tone: 'warn' },
          { label: 'HEALTHY', range: '≥ 75', tone: 'good' },
        ],
      },
      {
        id: 'schedule-score',
        title: 'Schedule Score',
        intent: 'Share of tasks with float ≥ 0, with data-quality and severity penalties.',
        inputs: [
          { name: 'Tasks with float', source: 'task_v.total_float_hr_cnt' },
          { name: 'On schedule', source: 'float_hr >= 0' },
        ],
        formula:
          'Base = (On-schedule tasks / Tasks with float) × 100 × (Tasks with float / Total tasks). Severity penalty: subtract min(20, avg |negative float| / 5).',
        thresholds: [
          { label: 'HEALTHY', range: '≥ 90', tone: 'good' },
          { label: 'AT RISK', range: '70–89', tone: 'warn' },
          { label: 'CRITICAL', range: '< 70', tone: 'bad' },
        ],
      },
      {
        id: 'work-performance',
        title: 'Work Performance Score',
        intent: 'Distance from ideal work ratio (actual / planned = 1.0).',
        formula: 'Work ratio = Actual work / Planned work (clamped 0.1–2.0). Score = 100 − (|ratio − 1| × 50).',
        thresholds: [
          { label: 'HEALTHY', range: '≥ 90', tone: 'good' },
          { label: 'AT RISK', range: '70–89', tone: 'warn' },
          { label: 'CRITICAL', range: '< 70', tone: 'bad' },
        ],
      },
      {
        id: 'resource-score',
        title: 'Resource Score',
        intent: 'Banded utilization: ideal 80–100%, at risk 60–79% or 101–120%.',
        formula: 'Utilization = (Actual hours) / (Planned hours) × 100. Score: 100 in 80–100%, 70 in 60–79% or 101–120%, linear below 60%, penalty above 120%.',
        thresholds: [
          { label: 'HEALTHY', range: '≥ 80', tone: 'good' },
          { label: 'AT RISK', range: '60–79', tone: 'warn' },
          { label: 'CRITICAL', range: '< 60', tone: 'bad' },
        ],
      },
      {
        id: 'quality-score',
        title: 'Quality Score',
        intent: 'Execution hygiene: penalties for completed with remaining work, unplanned work, low phys % complete, started-but-not-ended.',
        formula: 'Start 100; deduct up to 30 (remaining work on complete), 25 (unplanned work), 25 (low phys complete), 20 (started not ended).',
        thresholds: [
          { label: 'HEALTHY', range: '≥ 90', tone: 'good' },
          { label: 'AT RISK', range: '70–89', tone: 'warn' },
          { label: 'CRITICAL', range: '< 70', tone: 'bad' },
        ],
      },
      {
        id: 'scope-score',
        title: 'Scope Score',
        intent: 'Time-normalized completion: actual % complete vs expected by elapsed time.',
        formula: 'Expected % = (elapsed_days / project_duration_days) × 100. Score = 100 − max(0, (expected − actual) × 2).',
        caveats: ['If no project dates, raw completion % is used.'],
      },
      {
        id: 'risk-score',
        title: 'Risk Score',
        intent: 'Blend of negative-float severity, near-critical count, and missing-float data.',
        formula: 'Start 100; deduct up to 40 (negative float severity), 30 (near-critical share), 30 (tasks without float data).',
      },
    ],
  },

  // ─── DCMA 14-Point Assessment ──────────────────────────────────────────
  {
    id: 'dcma',
    label: 'DCMA 14-Point Assessment',
    description: 'DCMA-compliant schedule quality checks; data date from project_v (next_data_date / last_recalc_date).',
    blocks: [
      {
        title: 'Per-point rules (denominators and thresholds)',
        bullets: [
          '1. Logic: ≥95% of incomplete activities with both pred and succ. Denominator: incomplete, non-WBS/LOE/milestone.',
          '2. Leads: 0% negative lag. Denominator: all relationships.',
          '3. Lags: ≤5% positive lag. Denominator: all relationships.',
          '4. Finish-to-Start: ≥90% FS. Denominator: relationships with pred_type set.',
          '5. Hard constraints: ≤5% MSO/MFO on incomplete tasks.',
          '6. High float: ≤5% with float > 44 working days (calendar day_hr_cnt).',
          '7. Negative float: 0%. Denominator: incomplete with float data.',
          '8. High duration: ≤5% with original duration > 44 working days.',
          '9. Invalid dates: 0% (actuals in future, forecasts in past vs data_date).',
          '10. Resources: 100% assignable tasks with resources if resource-loaded; else N/A.',
          '11. Missed tasks: ≤5% of tasks with baseline finish ≤ data_date that are incomplete or finished late.',
          '12. Critical path test: Pass = finish milestone has zero float (simplified; full 600-day slip test optional).',
          '13. CPLI: 0.95–1.10 Pass, <0.95 Fail, >1.10 Warning; from finish milestone float.',
          '14. BEI: ≥95% Pass (tasks completed by data_date / tasks planned by data_date); 80–95% Warning.',
        ],
      },
      {
        title: 'Status',
        bullets: [
          'Pass / Fail / Warning per point. N/A when denominator is 0 or schedule not resource-loaded (Point 10).',
        ],
      },
    ],
  },

  // ─── Schedule Health Report ─────────────────────────────────────────────
  {
    id: 'schedule-health',
    label: 'Schedule Health Report',
    description: 'Single-record metrics from schedule.health query (SPI, status, variance, milestones, critical path, resources).',
    metrics: [
      {
        id: 'spi',
        title: 'Schedule Performance Index (SPI)',
        intent: 'Completion-based SPI: share of tasks that are complete.',
        formula: 'SPI = (Tasks with status TK_Complete) / (Total tasks). 1.0 = on track, <0.95 = at risk, else off track.',
        caveats: ['Not EV-based SPI (EV/PV); this is completion % as a 0–1 index.'],
      },
      {
        id: 'status',
        title: 'Schedule Status',
        intent: 'Derived from SPI.',
        formula: 'ON_TRACK: SPI ≥ 1.0. AT_RISK: SPI ≥ 0.95. OFF_TRACK: SPI < 0.95.',
      },
      {
        id: 'variance',
        title: 'Schedule Variance (days)',
        intent: 'Forecast finish vs baseline finish.',
        formula: 'Variance = (Forecast finish date − Baseline finish date) in days. Forecast = latest early_end_date; baseline = plan_end_date.',
      },
      {
        id: 'milestone-tracking',
        title: 'Milestone Tracking Counts',
        intent: 'Milestones (TT_Mile, TT_FinMile, TT_StartMile) by float.',
        formula: 'On track: float ≥ 0 and not complete. At risk: −24 < float < 0. Delayed: float ≤ −24 (hours).',
      },
      {
        id: 'float-utilization',
        title: 'Float Utilization',
        intent: 'Share of tasks with float that use all float (zero float).',
        formula: 'Float utilization % = (Tasks with float = 0) / (Tasks with float ≥ 0 or null) × 100.',
      },
    ],
  },

  // ─── Critical Path ──────────────────────────────────────────────────────
  {
    id: 'critical-path',
    label: 'Critical Path Report',
    description: 'Tasks on the critical path: total_float ≤ 0 or critical_flag = Y.',
    metrics: [
      {
        id: 'critical-definition',
        title: 'Critical Path Definition',
        intent: 'Include task if it drives or is driven by project finish with no slack.',
        formula: 'Task is critical if total_float_hr_cnt is null, empty, or ≤ 0; or critical_flag = Y.',
      },
      {
        id: 'path-position',
        title: 'Path Position',
        intent: 'START / MIDDLE / END by sequence order (early_start_date, task_code).',
      },
      {
        id: 'duration',
        title: 'Duration',
        intent: 'From target_drtn_hr_cnt (hours → days) or early_start to early_end.',
      },
    ],
  },

  // ─── High Float / Low Float ──────────────────────────────────────────────
  {
    id: 'high-float-low-float',
    label: 'High Float and Low Float Report',
    description: 'Two sections: high float ≥ threshold; low float between 1 and low threshold (hours). Incomplete tasks only.',
    metrics: [
      {
        id: 'high-float',
        title: 'High Float Section',
        formula: 'Include task if total_float_hr_cnt ≥ high_float_threshold_hr. Default high threshold: 120 hr (5 days).',
      },
      {
        id: 'low-float',
        title: 'Low Float Section',
        formula: 'Include task if 1 ≤ total_float_hr_cnt ≤ low_float_threshold_hr. Default low threshold: 120 hr (5 days).',
      },
    ],
  },

  // ─── Out of Sequence ────────────────────────────────────────────────────
  {
    id: 'out-of-sequence',
    label: 'Out-of-Sequence Work Report',
    description: 'FS relationships where successor started before predecessor finished (actual dates).',
    metrics: [
      {
        id: 'oos-definition',
        title: 'Out-of-Sequence Definition',
        intent: 'Finish-to-Start logic violated by actuals.',
        formula: 'Pred type PR_FS or FS; successor has act_start_date; predecessor has no act_end_date OR act_start_date(succ) < act_end_date(pred).',
      },
    ],
  },

  // ─── Near Critical ──────────────────────────────────────────────────────
  {
    id: 'near-critical',
    label: 'Near-Critical Path Report',
    description: 'Tasks with 0 < total_float ≤ threshold (hours), grouped by WBS. Default threshold 80 hr.',
    metrics: [
      {
        id: 'near-critical-definition',
        title: 'Near-Critical Definition',
        formula: '0 < total_float_hr_cnt ≤ float_threshold_hr. Excludes zero and negative float (those are critical).',
      },
    ],
  },

  // ─── Open Ends ──────────────────────────────────────────────────────────
  {
    id: 'open-ends',
    label: 'Open Ends (Missing Logic) Report',
    description: 'Non-milestone activities with missing predecessor and/or successor.',
    metrics: [
      {
        id: 'open-end-types',
        title: 'Open End Types',
        formula: 'missing_both: pred_cnt = 0 and succ_cnt = 0. missing_pred: pred_cnt = 0. missing_succ: succ_cnt = 0. Excludes WBS, LOE, milestones, zero-duration.',
      },
    ],
  },

  // ─── Lookahead ───────────────────────────────────────────────────────────
  {
    id: 'lookahead',
    label: 'Lookahead Schedule Report',
    description: 'Activities starting or finishing within a date window; incomplete only.',
    metrics: [
      {
        id: 'lookahead-window',
        title: 'Lookahead Window',
        formula: 'Anchor date (default today) to end_dt = anchor + lookahead_weeks (1, 2, or 4). Task included if target_start_date or target_end_date in [anchor, end_dt].',
      },
    ],
  },

  // ─── Baseline vs Current ─────────────────────────────────────────────────
  {
    id: 'baseline-vs-current',
    label: 'Baseline vs Current Schedule Report',
    description: 'Compare current snapshot to baseline snapshot (or base_* columns) per task.',
    metrics: [
      {
        id: 'finish-variance',
        title: 'Finish Variance',
        formula: 'finish_var_hr (days) = current target_end_date − baseline target_end_date. Positive = slipped.',
      },
      {
        id: 'duration-variance',
        title: 'Duration Variance',
        formula: 'dur_var_hr = current target_drtn_hr_cnt − baseline target_drtn_hr_cnt (hours).',
      },
      {
        id: 'sort',
        title: 'Sort Order',
        formula: 'Rows sorted by finish variance descending (biggest slip first).',
      },
    ],
  },

  // ─── Activity Slippage ───────────────────────────────────────────────────
  {
    id: 'activity-slippage',
    label: 'Activity Slippage Report',
    description: 'Tasks that slipped vs baseline: current finish > baseline finish; baseline required.',
    metrics: [
      {
        id: 'slip-days',
        title: 'Slip (days)',
        formula: 'slip_days = (current target_end_date − baseline target_end_date) in days. Only rows with slip_days > 0.',
      },
      {
        id: 'sort-slippage',
        title: 'Sort Order',
        formula: 'Sorted by slip descending (biggest slip first).',
      },
    ],
  },

  // ─── Milestone Status ───────────────────────────────────────────────────
  {
    id: 'milestone-status',
    label: 'Milestone Status Report',
    description: 'Milestones with planned/forecast/actual dates; filter by status (all, complete, in progress, not started).',
    metrics: [
      {
        id: 'milestone-definition',
        title: 'Milestone Definition',
        formula: 'task_type in (TT_Mile, TT_FinMile, TT_StartMile, TT_Milestone) OR target_drtn_hr_cnt = 0.',
      },
      {
        id: 'dates',
        title: 'Dates Shown',
        formula: 'Planned start/finish (target_*), forecast_finish (early_end_date), actual start/finish (act_*).',
      },
    ],
  },

  // ─── Milestone Delivery Scorecard ────────────────────────────────────────
  {
    id: 'milestone-delivery-scorecard',
    label: 'Milestone Delivery Scorecard',
    description: 'Summary counts and Top 10 late; optional baseline for on-time/late.',
    metrics: [
      {
        id: 'summary-counts',
        title: 'Summary Counts',
        formula: 'total_milestones; completed; completed_on_time (actual ≤ baseline finish); completed_late; upcoming_in_window (forecast in next N days, default 30); upcoming_at_risk (forecast > baseline).',
      },
      {
        id: 'top10-late',
        title: 'Top 10 Late',
        formula: 'Completed milestones with actual_finish > baseline_finish, sorted by slip (actual − baseline) descending, first 10.',
      },
    ],
  },

  // ─── Constraints ────────────────────────────────────────────────────────
  {
    id: 'constraints',
    label: 'Constraints Report',
    description: 'Tasks with a constraint type; variance (hours) between constraint date and logic-driven date.',
    metrics: [
      {
        id: 'variance-hr',
        title: 'Variance (hours)',
        formula: 'Variance = constraint_date − early_start (start constraints) or constraint_date − early_end (finish constraints), in hours.',
      },
    ],
  },

  // ─── Constraint Usage ───────────────────────────────────────────────────
  {
    id: 'constraint-usage',
    label: 'Constraint Usage Report',
    description: 'Full constraint inventory: type, dates, variance vs early dates, hard-constraint flag.',
    blocks: [
      {
        title: 'Metrics',
        bullets: [
          'Per task: constraint type, constraint date, early start/end, variance_vs_early (hours), hard_constraint_flag.',
        ],
      },
    ],
  },

  // ─── Calendar Usage ──────────────────────────────────────────────────────
  {
    id: 'calendar-usage',
    label: 'Calendar Usage Report',
    description: 'Calendars used in the project with activity counts and flags.',
    metrics: [
      {
        id: 'calendar-metrics',
        title: 'Per-Calendar Metrics',
        formula: 'clndr_id, clndr_name, activity_count, missing_calendar_flag, non_default_calendar_flag.',
      },
    ],
  },

  // ─── High Duration Tasks ─────────────────────────────────────────────────
  {
    id: 'high-duration-tasks',
    label: 'High Duration Tasks Report',
    description: 'Incomplete tasks with original duration > threshold. Default threshold 480 hr (20 days).',
    metrics: [
      {
        id: 'duration-threshold',
        title: 'Duration Threshold',
        formula: 'Include task if target_drtn_hr_cnt > duration_threshold_hr. Configurable in hours or days.',
      },
    ],
  },

  // ─── Relationship Density ──────────────────────────────────────────────
  {
    id: 'relationship-density',
    label: 'Relationship Density Report',
    description: 'Predecessor/successor counts per activity; under- and over-connected flags.',
    metrics: [
      {
        id: 'under-over',
        title: 'Under- and Over-Connected',
        formula: 'total_rels = pred_count + succ_count. Under-connected: total_rels ≤ under_connected_max (default 1). Over-connected: total_rels ≥ over_connected_min (default 7). Incomplete tasks only.',
      },
    ],
  },

  // ─── WBS Dictionary Metrics ──────────────────────────────────────────────
  {
    id: 'wbs-dictionary-metrics',
    label: 'WBS Dictionary and Metrics Report',
    description: 'WBS hierarchy with schedule metrics per node: activity counts, duration-weighted % complete, float, critical/near-critical counts.',
    metrics: [
      {
        id: 'duration-weighted-pct',
        title: 'Duration-Weighted % Complete',
        formula: 'Per WBS: sum(duration × pct_complete) / sum(duration) over child activities. Overall: same over all WBS nodes.',
      },
      {
        id: 'health',
        title: 'WBS Health',
        formula: 'Red: avg_float < 0 or critical_count > 0. Amber: near_critical_count > 0. Green: otherwise. Near-critical threshold configurable (default 80 hr).',
      },
    ],
  },

  // ─── Period Performance ──────────────────────────────────────────────────
  {
    id: 'period-performance',
    label: 'Period Performance Report',
    description: 'Activities that started or finished within a date period (default last 7 days).',
    metrics: [
      {
        id: 'period-window',
        title: 'Period Window',
        formula: 'period_start and period_end (from filters or default: end = today, start = end − 6 days). Task included if act_start_date or act_end_date in [period_start, period_end].',
      },
    ],
  },

  // ─── Schedule Health Summary ─────────────────────────────────────────────
  {
    id: 'schedule-health-summary',
    label: 'Schedule Health Summary Report',
    description: 'One-page executive summary counts: open ends, constraints, lags/leads, float, duration, invalid calendar, out-of-sequence.',
    metrics: [
      {
        id: 'summary-metrics',
        title: 'Summary Metrics',
        formula: 'active_tasks; open_ends; missing_pred_count; missing_succ_count; constrained_tasks; lag_rels; lead_rels; negative_float_tasks; high_float_tasks (float > high_float_hr, default 352); long_duration_tasks (dur > long_duration_hr, default 160); invalid_calendar_tasks; out_of_sequence_tasks. Incomplete tasks only.',
      },
    ],
  },

  // ─── Resource Utilization Report ─────────────────────────────────────────
  {
    id: 'resource-util',
    label: 'Resource Utilization Report',
    description: 'Utilization by resource/role from taskrsrc_v (target_qty, act_reg_qty, act_ot_qty).',
    metrics: [
      {
        id: 'utilization',
        title: 'Utilization by Role / Resource',
        formula: 'Utilization % = (Actual hours) / (Planned/allocated hours) × 100. Actual = act_reg_qty + act_ot_qty; planned = target_qty.',
        thresholds: [
          { label: 'LOW', range: '< 60%', tone: 'bad' },
          { label: 'HEALTHY', range: '60–100%', tone: 'good' },
          { label: 'OVER', range: '> 100%', tone: 'warn' },
        ],
      },
    ],
  },

  // ─── Data Sources ───────────────────────────────────────────────────────
  {
    id: 'data-sources',
    label: 'Data Sources',
    blocks: [
      {
        title: 'Primary XER Views / Tables Used',
        bullets: [
          'Tasks: task_v',
          'Resource assignments: taskrsrc_v',
          'Resources: rsrc_v',
          'Relationships: taskpred_v',
          'WBS: projwbs_v',
          'Project metadata: project_v',
          'Calendars: calendar_v',
        ],
      },
    ],
  },

  // ─── Important Notes ────────────────────────────────────────────────────
  {
    id: 'notes',
    label: 'Important Notes',
    blocks: [
      {
        title: 'Assumptions & Caveats',
        bullets: [
          'All calculations use the project Data Date (next_data_date / last_recalc_date from project_v), not system date, where applicable.',
          'Some metrics show 0% or N/A if denominator is zero or data is missing.',
          'Thresholds (e.g. high float, near-critical, duration) are configurable via report filters.',
          'Baseline comparisons require a baseline snapshot or base_* columns.',
        ],
      },
    ],
  },
];
